import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { I18nText } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';

const TeamModal = props => {
  const {
    signedInAsSuperAdmin,
    currentUsersAffiliateId,
    fetchTeamsByAffiliates,
    isNewRecord,
    teamInitialAttributes,
    buttonText,
    buttonClass,
    header,
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState(teamInitialAttributes.name);
  const [affiliate, setAffiliate] = useState(teamInitialAttributes.affiliate);
  const [users, setUsers] = useState(teamInitialAttributes.users);
  const [affiliatesList, setAffiliatesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [error, setError] = useState({});

  const fetchUsers = debounce(500, async inputValue => {
    if (inputValue.length > 1) {
      let requestRoute;

      if (signedInAsSuperAdmin) {
        requestRoute = routes.users.list({
          q: inputValue,
          affiliate_id: affiliate.value,
        });
      } else {
        requestRoute = routes.users.list({
          q: inputValue,
          affiliate_id: currentUsersAffiliateId,
        });
      }
      const { data } = await httpClient.get(requestRoute);

      const users = data.users.map(user => ({
        label: user.name,
        value: user.id,
      }));
      setUsersList(users);
    }
  });

  const fetchAffiliates = debounce(500, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.affiliates.list({ q: inputValue }),
      );

      const affiliates = data.affiliates.map(affiliate => ({
        label: affiliate.clientName,
        value: affiliate.id,
      }));
      setAffiliatesList(affiliates);
    }
  });

  const onAffiliateChange = selectedOption => {
    setAffiliate(selectedOption);
  };

  const onUserChange = selectedOptions => {
    setUsers(selectedOptions);
  };

  const onSubmitForm = async () => {
    let affiliateId;
    if (signedInAsSuperAdmin) {
      affiliateId = affiliate && affiliate.value;
    } else {
      affiliateId = currentUsersAffiliateId;
    }

    const teamParams = {
      name,
      affiliate_id: affiliateId,
      user_ids: (users && users.map(user => user.value)) || [],
    };

    let requestRoute;

    if (!isNewRecord) {
      const teamId = teamInitialAttributes.id;
      requestRoute = httpClient.put(
        routes.teams.update({ id: teamId }),
        teamParams,
      );
    } else {
      requestRoute = httpClient.post(routes.teams.create(), teamParams);
    }

    try {
      const { data } = await requestRoute;

      if (data.error === null) {
        setModalOpen(false);
        fetchTeamsByAffiliates();
      }
    } catch (error) {
      const response = error.response;
      if (response && response.status === 422) {
        setError(response.data.error);
      } else {
        throw error;
      }
    }
  };

  return (
    <>
      <Button
        className={buttonClass}
        version="v2"
        size="small"
        fullWidth={false}
        type="primary"
        label={buttonText}
        onClick={() => {
          setModalOpen(true);
        }}
      />
      <Modal
        className="modal__wrapper modal__wrapper-overflow modal__wrapper-small"
        isVisible={modalOpen}
        onOutsideClick={() => setModalOpen(false)}
      >
        <>
          <div className="modal__header">
            <Modal.Title>{header}</Modal.Title>
            <div
              onClick={() => setModalOpen(false)}
              onKeyDown={() => setModalOpen(false)}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <Modal.Content>
            <div className="grid">
              <div className="col-12 col-bleed-x">
                <Input
                  value={name}
                  onChange={value => setName(value)}
                  placeholder={I18n.t('components.team.form.placeholders.name')}
                  error={error.name}
                  touched={true}
                  isClearable={false}
                />
              </div>
              {signedInAsSuperAdmin && (
                <div className="col-12 col-bleed-x">
                  <SelectBox
                    onChange={onAffiliateChange}
                    onInputChange={fetchAffiliates}
                    options={affiliatesList}
                    placeholderText={
                      <I18nText id="team.form.placeholders.affiliate" />
                    }
                    required={false}
                    width="full"
                    value={affiliate}
                    errorMsg={error.affiliate}
                  />
                </div>
              )}

              <div className="col-12 col-bleed-x">
                <SelectBox
                  className="team-index__team-modal-select-members"
                  onChange={onUserChange}
                  onInputChange={fetchUsers}
                  options={usersList}
                  placeholderText={
                    <I18nText id="team.form.placeholders.members" />
                  }
                  required={false}
                  isMulti={true}
                  width="full"
                  value={users}
                  isDisabled={signedInAsSuperAdmin && !affiliate}
                  errorMsg={error.user}
                  maxMenuHeight={200}
                />
              </div>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <div className="col-12 col-bleed">
              <div className="team-index__team-modal-save">
                <Button
                  label={<I18nText id="team.actions.save" />}
                  size="small"
                  version="v2"
                  type="primary"
                  onClick={onSubmitForm}
                />
              </div>

              <Button
                label={<I18nText id="team.actions.cancel" />}
                size="small"
                version="v2"
                onClick={() => {
                  setModalOpen(false);
                }}
              />
            </div>
          </Modal.Footer>
        </>
      </Modal>
    </>
  );
};

TeamModal.propTypes = {
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  currentUsersAffiliateId: PropTypes.number.isRequired,
  fetchTeamsByAffiliates: PropTypes.func.isRequired,
  isNewRecord: PropTypes.bool.isRequired,
  teamInitialAttributes: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    affiliate: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    users: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }).isRequired,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  buttonClass: PropTypes.string.isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
};

export default TeamModal;
